import {
  colors,
  Flex,
  Heading,
  Text,
  TextLink,
} from '@weareberyl/design-system'
import {
  Card,
  Descriptions,
  message,
  Popover,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import HeadTitle from '~/components/HeadTitle'
import Loading from '~/components/Loading'
import * as routes from '~/constants/routes'
import {
  ActionAutomationFlowConfiguration,
  AutomationFlowsQuery_sourced_automation_flows_SourcedAutomationFlow as AutomationFlow,
  JobAutomationFlowConfiguration,
  PublishAutomationFlowConfiguration,
  useAutomationFlowsQuery,
} from '~/gql/generated/graphql'
import AutomationFilters from './AutomationFilters'
import { useJobTypes } from '~/hooks'
import { ClockCircleOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { capitalCase } from 'change-case'
import BooleanIcon from '~/components/shared/BooleanIcon'
import * as prettier from 'prettier/standalone'
import * as graphql from 'prettier/plugins/graphql'
import { useHistory } from 'react-router'
import { useQueryParams } from '~/utils/useQueryParams'
import { sortBy } from 'lodash'

const Task = ({ id }: { id: string }) => {
  const { jobCodeLookup } = useJobTypes()
  return jobCodeLookup?.[id] ?? ''
}

const Tags = ({
  tags,
  icon,
  renderTagText,
}: {
  tags: string[] | null
  icon?: ReactNode
  renderTagText?: (value: string) => string
}) => {
  if (!tags || tags.length === 0) return null

  return (
    <Flex>
      {tags.map(s => (
        <Tag key={s} icon={icon}>
          {renderTagText?.(s) ?? s}
        </Tag>
      ))}
    </Flex>
  )
}

const Mutation = ({
  mutation_name,
  mutation,
}: {
  mutation_name: string
  mutation: string
}) => {
  const [content, setContent] = useState('')
  useEffect(() => {
    ;(async () => {
      const prettyMutation = await prettier.format(mutation, {
        parser: 'graphql',
        plugins: [graphql],
      })
      setContent(prettyMutation)
    })()
  }, [mutation])
  return (
    <Popover
      placement="bottom"
      content={
        <Flex style={{ padding: 2, backgroundColor: colors.tofu }}>
          <Text style>{content}</Text>
        </Flex>
      }
    >
      <Typography.Text code>{mutation_name}</Typography.Text>
    </Popover>
  )
}

const commonColumns: ColumnProps<AutomationFlow>[] = [
  {
    title: 'Enabled',
    dataIndex: ['dry_run'],
    render: dry_run => <BooleanIcon value={!dry_run} />,
  },
  {
    title: 'Tags',
    dataIndex: ['tags'],
    render: tags => (
      <Tags
        tags={tags}
        icon={<ClockCircleOutlined />}
        renderTagText={tag => `${tag} min(s)`}
      />
    ),
    sorter: (a, b) => ((a.tags?.[0] ?? '') > (b.tags?.[0] ?? '') ? 1 : -1),
  },
  {
    title: 'Filters',
    dataIndex: ['source', 'filters'],
    key: 'filters',
    render: filters => <AutomationFilters filters={filters} />,
  },
]

const Description = ({
  description,
  justification_id,
}: {
  description: string
  justification_id: string
}) => {
  const history = useHistory()
  return (
    <TextLink
      onPress={() =>
        history.push(
          `${routes.ALL_JOBS}?id=jobs-table&justification_id=${justification_id}`,
        )
      }
    >
      {description}
    </TextLink>
  )
}

const actionFlowsColumns: ColumnProps<
  Omit<AutomationFlow, 'configuration'> & {
    configuration: ActionAutomationFlowConfiguration
  }
>[] = [
  {
    title: 'Name',
    dataIndex: ['id'],
    key: 'id',
    render: id => capitalCase(id),
    sorter: (a, b) => (a.id > b.id ? 1 : -1),
  },
  {
    title: 'Mutation',
    dataIndex: ['configuration', 'mutation_name'],
    key: 'mutation_name',
    render: (mutation_name, object) => (
      <Mutation
        mutation_name={mutation_name}
        mutation={object.configuration.mutation}
      />
    ),
  },
]

const renderMessage = (msg: Record<string, unknown>) => {
  const renderItems = (items: object, depth = 0) => {
    if (depth > 4) return JSON.stringify(items)

    const renderContent = (content: unknown) => {
      switch (typeof content) {
        case 'object':
          depth++
          return renderItems(content as object, depth)
        case 'boolean':
          return <BooleanIcon value={content} />
        default:
          return JSON.stringify(content, null, 2)
      }
    }
    return (
      <Descriptions
        size="small"
        style={{ opacity: Math.pow(0.7, depth) }}
        bordered
      >
        {Object.entries(items).map(([key, val]) => (
          <Descriptions.Item
            span={5}
            key={`${key}${JSON.stringify(val)}`}
            label={capitalCase(key)}
            labelStyle={{ minWidth: 50 }}
            style={{ padding: 5 }}
            contentStyle={{ maxWidth: 300 }}
          >
            {renderContent(val)}
          </Descriptions.Item>
        ))}
      </Descriptions>
    )
  }

  return renderItems(msg)
}

const publishFlowsColumns: ColumnProps<
  Omit<AutomationFlow, 'configuration'> & {
    configuration: PublishAutomationFlowConfiguration
  }
>[] = [
  {
    title: 'Event',
    dataIndex: ['configuration', 'eventType'],
    key: 'eventType',
    render: eventType => capitalCase(eventType),
  },
  {
    title: 'Topic',
    dataIndex: ['configuration', 'topic_name'],
    key: 'topic',
  },
  {
    title: 'Message',
    dataIndex: ['configuration', 'message_data'],
    key: 'message',
    render: renderMessage,
  },
]

const Automations = () => {
  const { data, error, loading } = useAutomationFlowsQuery({
    pollInterval: 0,
    fetchPolicy: 'cache-first',
  })
  const [{ tab, current, pageSize }, setQueryParams] = useQueryParams(
    'automations-view',
    {
      tab: 'job',
      current: '1',
      pageSize: 20,
    },
  )

  useEffect(() => {
    if (error) {
      message.error(error)
    }
  }, [error])

  const jobAutomationFlows = useMemo(
    () =>
      data?.sourced_automation_flows.filter(
        ({ configuration }) =>
          configuration.__typename === 'JobAutomationFlowConfiguration',
      ),
    [data],
  )
  const actionAutomationFlows = useMemo(
    () =>
      data?.sourced_automation_flows.filter(
        ({ configuration }) =>
          configuration.__typename === 'ActionAutomationFlowConfiguration',
      ),
    [data],
  )

  const publishAutomationFlows = useMemo(
    () =>
      data?.sourced_automation_flows.filter(
        ({ configuration }) =>
          configuration.__typename === 'PublishAutomationFlowConfiguration',
      ),
    [data],
  )

  const Header = () => (
    <Flex justifyContent="space-between" alignItems="flex-end" mb={5}>
      <Heading variant="callout">Automations</Heading>
    </Flex>
  )
  const Content = () => {
    const { jobCodeLookup } = useJobTypes()

    const taskIdFilters = useMemo(
      () =>
        jobCodeLookup
          ? sortBy(
              Object.entries(jobCodeLookup)
                .map(([v, k]) => ({
                  value: v,
                  text: k,
                }))
                .filter(({ value }) =>
                  jobAutomationFlows
                    ?.map(
                      f =>
                        (f.configuration as JobAutomationFlowConfiguration)
                          .task_id,
                    )
                    ?.includes(value),
                ),
              ({ text }) => text,
            )
          : undefined,
      [jobCodeLookup, jobAutomationFlows],
    )

    const jobFlowsColumns = useMemo(
      () =>
        (
          [
            {
              title: 'Description',
              dataIndex: ['configuration', 'description'],
              key: 'description',
              render: (description, object) => (
                <Description
                  description={description}
                  justification_id={object.id}
                />
              ),
              sorter: (a, b) =>
                a.configuration.description > b.configuration.description
                  ? 1
                  : -1,
            },
            {
              title: 'Job Type',
              dataIndex: ['configuration', 'task_id'],
              render: id => <Task id={id} />,
              filters: taskIdFilters,
              onFilter: (value, record) =>
                record.configuration.task_id === (value as string),
              sorter: (a, b) =>
                a.configuration.task_id > b.configuration.task_id ? 1 : -1,
            },
            {
              title: 'Side Effects',
              dataIndex: ['configuration', 'side_effects'],
              render: side_effects => (
                <Tags
                  tags={side_effects}
                  icon={<ThunderboltOutlined />}
                  renderTagText={capitalCase}
                />
              ),
              sorter: (a, b) =>
                (a.configuration.side_effects?.[0] ?? '') >
                (b.configuration.side_effects?.[0] ?? '')
                  ? 1
                  : -1,
            },
            {
              title: 'Use Cleanup',
              dataIndex: ['configuration', 'use_cleanup'],
              render: use_cleanup => <BooleanIcon value={use_cleanup} />,
            },
          ] as ColumnProps<
            Omit<AutomationFlow, 'configuration'> & {
              configuration: JobAutomationFlowConfiguration
            }
          >[]
        ).concat(commonColumns),
      [taskIdFilters],
    )

    const onPaginationChange = (newCurrent, newPageSize) => {
      if (current !== newCurrent || pageSize !== newPageSize)
        setQueryParams({ current: newCurrent, pageSize: newPageSize })
    }

    if (loading) return <Loading />

    const items = [
      {
        key: 'job',
        label: 'Job Automations',
        children: (
          <Table
            dataSource={jobAutomationFlows}
            columns={jobFlowsColumns}
            pagination={{
              current,
              pageSize,
              onChange: onPaginationChange,
            }}
            scroll={{ x: 'max-content' }}
          />
        ),
      },
      {
        key: 'action',
        label: 'Action Automations',
        children: (
          <Table
            dataSource={actionAutomationFlows}
            columns={actionFlowsColumns.concat(commonColumns)}
            pagination={{
              current,
              pageSize,
              onChange: onPaginationChange,
            }}
            scroll={{ x: 'max-content' }}
          />
        ),
      },
      {
        key: 'publish',
        label: 'Publish Automations',
        children: (
          <Table
            dataSource={publishAutomationFlows}
            columns={publishFlowsColumns.concat(commonColumns)}
            pagination={{
              current,
              pageSize,
              onChange: onPaginationChange,
            }}
            scroll={{ x: 'max-content' }}
          />
        ),
      },
    ]

    return (
      <Tabs
        items={items}
        activeKey={tab}
        onTabClick={key => setQueryParams({ tab: key })}
      />
    )
  }

  return (
    <>
      <HeadTitle pageTitle="Automations" />
      <Card style={{ padding: 5 }}>
        <Header />
        <Content />
      </Card>
    </>
  )
}

export default Automations
